import { Loader2 } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AuditFixDialog } from '@/components/AuditFixDialog';
import { YmsAlertDialog } from '@/components/dashboard/YmsAlertDialog';
import { convertCurrentTimeToPSTFormat } from '@/components/helper';
import { useToast } from '@/components/ui';
import { IMAGE_CONSTANTS } from '@/constants/imageConstants';
import { taskcontants } from '@/constants/taskConstants';
import { CreateTasks, getLocationList } from '@/service/ManhattanService';
import {
  CompleteActionException,
  CreateAuditTaskUpdateTaskDetails,
  UpdateTaskResponseUpdateTaskDetail,
} from '@/service/types/audittasktypes';
import { MoveLocationDetail } from '@/service/types/movetasktypes';
import { store, useAppSelector, selectUser, selectPlant } from '@/store';
import { cn } from '@/utils/cn';
import { convertPSTToIST } from '@/utils/dateUtils';

export function CreateAuditTaskPage() {
  const listRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [locations, setLocations] = useState<MoveLocationDetail[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [filterType, setFilterType] = useState('All');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [selectedFixLocation, setSelectedFixLocation] =
    useState<CreateAuditTaskUpdateTaskDetails | null>(null);
  const [auditConfirmOpen, setAuditConfirmOpen] = useState(false);
  const [auditConfirmMessage, setAuditConfirmMessage] = useState('');
  const [pendingFixLocation, setPendingFixLocation] =
    useState<CreateAuditTaskUpdateTaskDetails | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const plant = useAppSelector(selectPlant);
  const user = useAppSelector(selectUser);
  const orgId = String(plant?.org_id ?? '');
  const countrycode = user?.country_code ?? '';
  const mobilenumber = user?.mobile_no ?? '';
  const userId = countrycode + mobilenumber;
  const [allTaskList, setAllTaskList] = useState<CreateAuditTaskUpdateTaskDetails[]>([]);
  const [filteredTaskList, setFilteredTaskList] = useState<CreateAuditTaskUpdateTaskDetails[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [exceptionList, setExceptionList] = useState<CompleteActionException | null>(null);
  const [isFixComplete, setIsfixcomplete] = useState(false);
  const [warningPopup, setWarningPopup] = useState(false);
  const [completeTaskMessage, setCompleteTaskMessage] = useState('');

  useEffect(() => {
    void loadLocations();
    GetExceptionList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function GetExceptionList() {
    const exceptionList = store.getState().global.settings?.COMPLETE_ACTION_EXCEPTION ?? '';
    if (!exceptionList) return;
    try {
      const exceptionListdata = JSON.parse(exceptionList) as CompleteActionException;
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('exceptionListdata', exceptionListdata);
      }
      setExceptionList(exceptionListdata);
    } catch {
      if (import.meta.env.DEV) {
        console.warn('Failed to parse COMPLETE_ACTION_EXCEPTION setting');
      }
    }
  }

  async function loadLocations() {
    setLocationsLoading(true);
    try {
      const locationList = await getLocationList('All');
      setLocations(locationList);
      const AllLocationsTaskList: CreateAuditTaskUpdateTaskDetails[] = [];

      for (const location of locationList) {
        {
          let filter = '';
          if (location.LocationType === taskcontants.YardSlotFilterItem) {
            filter = taskcontants.YardSlotFilterItem;
          } else {
            filter = taskcontants.DockDoorFilterItem;
          }

          AllLocationsTaskList.push({
            OrgId: orgId,
            UserId: userId,
            TaskType: taskcontants.AuditTaskType,
            AuditLocationName: location.LocationName,
            AuditLocationType: location.LocationType,
            AuditLocationZone: location.LocationZone,
            TrailerName: location.TrailerName,
            CarrierCode: location.CarrierCode,
            CarrierName: location.CarrierName,
            LastAuditTime: location.LastAuditTime,
            _trailerName: '',
            _carrierName: '',
            ActionType: '',
            ExceptionCode: '',
            AlternateTrailerValid: 'false',
            AlternateCarrierCode: '',
            AlternateTrailerName: '',
            AuditLocation: '',
            FilterType: filter,
            TrailerComments: '',
          });
        }
      }
      setAllTaskList(AllLocationsTaskList);
      setFilteredTaskList(AllLocationsTaskList);
    } catch {
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  }

  const FILTER_OPTIONS = [
    { FilterName: t('createAuditTask.filterAll'), FilterType: 'All' },
    { FilterName: t('createAuditTask.filterDockDoor'), FilterType: 'Door' },
    { FilterName: t('createAuditTask.filterYardSlot'), FilterType: 'Yard' },
  ];

  type FilterOption = (typeof FILTER_OPTIONS)[number];

  function getFilteredByDropdown(filter: string): CreateAuditTaskUpdateTaskDetails[] {
    if (filter === 'All') {
      return allTaskList;
    }
    return allTaskList.filter((x) => x.FilterType === filter);
  }

  function handleFilterChange(selectedFilter: FilterOption) {
    setFilterType(selectedFilter.FilterType);
    setDropdownOpen(false);
    setSearchQuery('');
    setFilteredTaskList(getFilteredByDropdown(selectedFilter.FilterType));
  }

  interface AuditLocationCardProps {
    location: CreateAuditTaskUpdateTaskDetails;
  }
  function AuditLocationCard({ location }: AuditLocationCardProps) {
    const hasTrailer = location.TrailerName.trim() !== '';
    const locationDisplay = `${location.AuditLocationZone} - ${location.AuditLocationName} (${location.AuditLocationType})`;

    return (
      <div className="mx-[10px] rounded-lg border border-task-border bg-white p-4">
        <div className="flex gap-4">
          {/* Dock icon */}
          <div className="flex-shrink-0 pt-1">
            <img
              src={
                hasTrailer
                  ? IMAGE_CONSTANTS.dockTrailer
                  : location.AuditLocationType == 'Yard'
                    ? IMAGE_CONSTANTS.yard
                    : IMAGE_CONSTANTS.dockEmpty
              }
              alt={hasTrailer ? 'Dock with trailer' : 'Empty dock'}
              className="h-10 w-12"
            />
          </div>

          {/* Location details */}
          <div className="flex-1 min-w-0">
            {/* Location */}
            <div className="mb-2">
              <span className="text-xs font-poppins text-task-label">
                {t('createAuditTask.location')}
              </span>
              <span className="text-xs font-poppins font-bold text-task-details">
                {locationDisplay}
              </span>
            </div>

            {/* Trailer Number & Carrier Name (only if trailer present) */}
            {hasTrailer && (
              <div className="flex flex-wrap gap-x-8 gap-y-1">
                <div>
                  <p className="text-xs font-poppins text-task-label">
                    {t('createAuditTask.trailerNumber')}
                  </p>
                  <p className="text-xs font-poppins font-bold text-task-details">
                    {location.TrailerName}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-poppins text-task-label">
                    {t('createAuditTask.carrierName')}
                  </p>
                  <p className="text-xs font-poppins font-bold text-task-details">
                    {location.CarrierName}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end mt-3 gap-3">
          <button
            type="button"
            onClick={() => {
              void handleAllGood(location);
            }}
            className="h-9 w-[120px] rounded-md bg-[#003366] text-white text-sm font-poppins font-semibold"
          >
            {t('createAuditTask.allGood')}
          </button>
          <button
            type="button"
            onClick={() => {
              handleFix(location);
            }}
            className="h-9 w-[120px] rounded-md border border-[#003366] text-[#003366] text-sm font-poppins font-semibold bg-white hover:bg-gray-50 transition-colors"
          >
            {t('createAuditTask.fix')}
          </button>
        </div>
      </div>
    );
  }

  function handleSearchInput(query: string) {
    const baseList = getFilteredByDropdown(filterType);
    if (!query.trim()) {
      setFilteredTaskList(baseList);
    } else {
      const q = query.toLowerCase();
      const searchedlist = baseList.filter((l) => {
        const displayName =
          `${l.AuditLocationZone} - ${l.AuditLocationName} (${l.AuditLocationType})`.toLowerCase();
        return (
          displayName.includes(q) ||
          l.TrailerName.toLowerCase().includes(q) ||
          l.CarrierName.toLowerCase().includes(q)
        );
      });
      setFilteredTaskList(searchedlist);
    }
  }

  const handleAllGood = async (location: CreateAuditTaskUpdateTaskDetails) => {
    {
      if (isSubmitting) return;
      if (checkIfContinueWithAudit(location, 'allgood')) {
        location.ActionType = taskcontants.AuditTaskType;
        await ExecuteAudit('allgood', location);
      }
    }
  };

  function checkIfContinueWithAudit(
    location: CreateAuditTaskUpdateTaskDetails,
    type: string
  ): boolean {
    setPendingFixLocation(location);
    const validateLastAudit = locations.find(
      (l) =>
        l.LocationName === location.AuditLocationName &&
        l.LocationType === location.AuditLocationType &&
        l.LocationZone === location.AuditLocationZone
    );
    if (
      validateLastAudit != null &&
      validateLastAudit.LastAuditTime.trim() !== '' &&
      new Date(
        convertPSTToIST(validateLastAudit.LastAuditTime).getTime() +
          Number(store.getState().global.settings?.AUDIT_TASK_COMPLETION_TIME_LIMIT ?? '3600') *
            1000
      ) > new Date()
    ) {
      const settings = store.getState().global.settings;
      const auditTimeLimitSeconds = Number(settings?.AUDIT_TASK_COMPLETION_TIME_LIMIT ?? '3600');
      const timeLimitHours = Math.floor(auditTimeLimitSeconds / 3600);
      if (type === 'fix') {
        setIsfixcomplete(true);
      } else {
        setIsfixcomplete(false);
      }
      setAuditConfirmOpen(true);
      setAuditConfirmMessage(
        t('createAuditTask.auditTimeLimitWarning', { hours: String(timeLimitHours) })
      );
      return false;
    } else {
      return true;
    }
  }

  // Handle "Fix" button — open fix dialog or show confirmation
  function handleFix(location: CreateAuditTaskUpdateTaskDetails) {
    if (checkIfContinueWithAudit(location, 'fix')) {
      setSelectedFixLocation(location);
      setFixDialogOpen(true);
    }
  }

  async function handleAuditConfirmYes() {
    setAuditConfirmOpen(false);
    if (pendingFixLocation) {
      pendingFixLocation.ActionType = taskcontants.AuditTaskType;
      if (isFixComplete) {
        setSelectedFixLocation(pendingFixLocation);
        setFixDialogOpen(true);
      } else {
        await ExecuteAudit('allgood', pendingFixLocation);
      }
    }
  }

  function handleAuditConfirmNo() {
    setAuditConfirmOpen(false);
    setPendingFixLocation(null);
  }

  function handleSearch(searchstring: string) {
    setSearchQuery(searchstring);
    handleSearchInput(searchstring);
  }

  const handleFixDialogClose = () => {
    setFixDialogOpen(false);
    setSelectedFixLocation(null);
    setIsfixcomplete(false);
  };

  const FixCompleteCommandHandler = async (task: UpdateTaskResponseUpdateTaskDetail) => {
    const foundTask = allTaskList.find(
      (x) => x.AuditLocationName === task.Task_details.Audit_location_name
    );
    if (!foundTask) return;
    if (isSubmitting) return;
    const updatedTask = { ...foundTask };
    updatedTask.ActionType = taskcontants.AuditTaskType;
    updatedTask.ExceptionCode = task.Exception_code;
    updatedTask.AlternateTrailerValid = task.Task_details.Alt_Trailer_Valid;
    updatedTask.AlternateTrailerName = task.Task_details.Alt_Trailer;
    updatedTask.AlternateCarrierCode = task.Task_details.Alt_Carrier;
    updatedTask.TrailerComments = task.Trailer_comments;
    await ExecuteAudit('fix', updatedTask);
  };

  async function ExecuteAudit(type: string, location: CreateAuditTaskUpdateTaskDetails) {
    setIsSubmitting(true);
    try {
      const plantId = store.getState().user.plant?._id ?? '';
      const sessionId = store.getState().user.sessionId ?? '';
      const createAuditTaskRequest = {
        payload: {
          TransmissionHeader: {
            TransmissionDateTime: convertCurrentTimeToPSTFormat(),
            OriginatingSystem: taskcontants.OriginatingSystem,
            TransmissionUser: 'nblyms',
            RoutingCode: taskcontants.RoutingCode,
            MessageType: taskcontants.messageTypeUpdateTasks,
            MessageId: taskcontants.MessageId,
          },
          Body: {
            OrgId: orgId,
            StopOnFirstActionError: taskcontants.StopOnFirstError,
            UpdateTaskDetails: location,
          },
        },
        TraceData: {
          User: userId,
          Plant: plantId,
          SessionId: sessionId,
        },
      };

      const completeTaskResponse = await CreateTasks(createAuditTaskRequest);
      if (completeTaskResponse.payload.Body.Status === taskcontants.SuccessStatus) {
        const updateDetails = completeTaskResponse.payload.Body.Update_task_details ?? [];
        if (updateDetails.length > 0 && updateDetails[0]?.Status === taskcontants.SuccessStatus) {
          if (type === 'fix') {
            setFixDialogOpen(false);
            showToast(
              t('createAuditTask.auditCreatedSuccessfully', {
                zone: location.AuditLocationZone,
                name: location.AuditLocationName,
              }),
              { type: 'success' }
            );
          } else if (type === 'allgood') {
            showToast(
              t('createAuditTask.auditedSuccessfully', {
                zone: location.AuditLocationZone,
                name: location.AuditLocationName,
              }),
              { type: 'success' }
            );
          }

          // // Refresh data for only the audited location
          // console.log('Before refresh', window.scrollY);
          // const liveLocations = await getLocationList('All');
          // console.log('After refresh', window.scrollY);

          // const updatedTask = liveLocations.find(
          //   (x) =>
          //     x.LocationName === location.AuditLocationName &&
          //     x.LocationZone === location.AuditLocationZone &&
          //     x.LocationType === location.AuditLocationType
          // );

          const currentScroll = listRef.current?.scrollTop ?? 0;
          const liveLocations = await getLocationList('All');
          const updatedTask = liveLocations.find((x) =>
          x.LocationName === location.AuditLocationName &&
          x.LocationZone === location.AuditLocationZone &&
          x.LocationType === location.AuditLocationType);
 
          setTimeout(() => {
          if (listRef.current) {
          listRef.current.scrollTop = currentScroll;
          }
          }, 0);

          if (updatedTask) {
            setAllTaskList((prev) =>
              prev.map((item) =>
                item.AuditLocationName === location.AuditLocationName &&
                item.AuditLocationZone === location.AuditLocationZone &&
                item.AuditLocationType === location.AuditLocationType
                  ? {
                      ...item,
                      TrailerName: updatedTask.TrailerName,
                      CarrierName: updatedTask.CarrierName,
                      CarrierCode: updatedTask.CarrierCode,
                      LastAuditTime: updatedTask.LastAuditTime,
                    }
                  : item
              )
            );

            setFilteredTaskList((prev) =>
              prev.map((item) =>
                item.AuditLocationName === location.AuditLocationName &&
                item.AuditLocationZone === location.AuditLocationZone &&
                item.AuditLocationType === location.AuditLocationType
                  ? {
                      ...item,
                      TrailerName: updatedTask.TrailerName,
                      CarrierName: updatedTask.CarrierName,
                      CarrierCode: updatedTask.CarrierCode,
                      LastAuditTime: updatedTask.LastAuditTime,
                    }
                  : item
              )
            );
          }
        } else {
          setFixDialogOpen(false);
          setWarningPopup(true);
          setCompleteTaskMessage(
            updateDetails[0]?.Status_message ?? t('createAuditTask.failedToCompleteAudit')
          );
        }
      } else if (
        completeTaskResponse.payload.Body.Update_task_details &&
        completeTaskResponse.payload.Body.Update_task_details.length > 0
      ) {
        setFixDialogOpen(false);
        setWarningPopup(true);
        setCompleteTaskMessage(
          completeTaskResponse.payload.Body.Update_task_details[0]?.Status_message ??
            t('createAuditTask.failedToCompleteAudit')
        );
      } else {
        setFixDialogOpen(false);
        showToast(completeTaskResponse.payload.Body.Status_message, { type: 'error' });
      }
    } catch (error) {
      setFixDialogOpen(false);
      const message =
        error instanceof Error ? error.message : t('createAuditTask.failedToCompleteAudit');
      showToast(message, { type: 'error' });
    } finally {
      // setSearchQuery('');
      // setFilterType('All');
      // setFilteredTaskList(allTaskList);
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search bar + Filter dropdown */}
      <div className="py-2 flex gap-2 px-[10px]">
        {/* Search input */}
        <div className="w-[75%] flex-1 rounded-[3px] border border-searchbar-border bg-white">
          <input
            type="text"
            placeholder={t('createAuditTask.search')}
            value={searchQuery}
            onChange={(e) => {
              handleSearch(e.target.value);
            }}
            className="h-8 w-full bg-transparent pl-[10px] pr-2 text-xs font-poppins text-task-details placeholder:text-task-label outline-none"
          />
        </div>

        {/* Filter dropdown */}
        <div className="w-[25%] relative">
          <button
            type="button"
            onClick={() => {
              setDropdownOpen(!dropdownOpen);
            }}
            className="h-8 w-full rounded-[3px] border border-searchbar-border bg-white px-3 text-xs font-poppins text-task-details flex items-center justify-between"
          >
            <span className="truncate">
              {FILTER_OPTIONS.find((o) => o.FilterType === filterType)?.FilterName ?? filterType}
            </span>
            <svg
              className={cn(
                'h-4 w-4 text-task-label transition-transform',
                dropdownOpen && 'rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-11 z-10 w-full rounded-md border border-gray-300 bg-white shadow-lg">
              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option.FilterName}
                  type="button"
                  onClick={() => {
                    handleFilterChange(option);
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-xs font-poppins text-task-details  transition-colors',
                    filterType === option.FilterType
                      ? 'bg-[#91c1e6] hover:bg-[#60a9e0] font-normal'
                      : 'hover:bg-[#d9d9d9]'
                  )}
                >
                  {option.FilterName}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Location list */}
      <div ref={listRef} className="flex-1 overflow-y-auto">
        {locationsLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-button" />
          </div>
        ) : filteredTaskList.length > 0 ? (
          <div className="space-y-3 pb-4">
            {filteredTaskList.map((location) => (
              <AuditLocationCard
                key={`${location.AuditLocationZone}-${location.AuditLocationName}-${location.AuditLocationType}`}
                location={location}
              />
            ))}
          </div>
        ) : (
          <p className="text-center text-sm font-poppins text-task-label py-16">
            {searchQuery.trim()
              ? t('createAuditTask.noLocationsMatch')
              : t('createAuditTask.noLocationsAvailable')}
          </p>
        )}
      </div>

      <YmsAlertDialog
        open={warningPopup}
        title={t('createAuditTask.warning')}
        message={completeTaskMessage}
        okText={t('common.ok')}
        onOk={() => {
          setWarningPopup(false);
        }}
      />

      {/* Audit Confirmation Dialog — recently audited location */}
      <YmsAlertDialog
        open={auditConfirmOpen}
        title={t('createAuditTask.alert')}
        message={auditConfirmMessage}
        okText={t('common.yes')}
        cancelText={t('common.no')}
        showCancel
        onOk={handleAuditConfirmYes}
        onCancel={handleAuditConfirmNo}
      />

      {/* Audit Fix Dialog */}
      <AuditFixDialog
        screen={'CreateAudit'}
        open={fixDialogOpen}
        task={selectedFixLocation}
        onClose={handleFixDialogClose}
        onComplete={FixCompleteCommandHandler}
        CompleteActionExceptions={exceptionList}
      />
    </div>
  );
}
